import React from 'react';
import { ShoppingBagIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

/**
 * Shopping Ad Info Step
 *
 * For Shopping campaigns, ads are automatically generated from the
 * Google Merchant Center product feed. This step explains that to the user.
 */

export const ShoppingAdInfoStep: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Shopping Ads</h3>
        <p className="text-sm text-gray-600">
          Shopping campaigns use product data from Google Merchant Center to automatically create ads.
        </p>
      </div>

      {/* Ad Type Info */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center">
          <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
            <ShoppingBagIcon className="h-4 w-4 text-green-600" />
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-900">Product Shopping Ads</span>
            <p className="text-xs text-gray-500">
              Ads are automatically generated from your Merchant Center product feed
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="space-y-2">
            <p className="text-sm text-blue-800">
              <span className="font-medium">No manual ad creation required.</span>
            </p>
            <p className="text-sm text-blue-700">
              Shopping ads are created automatically using:
            </p>
            <ul className="text-sm text-blue-700 list-disc list-inside ml-2 space-y-1">
              <li>Product images from your feed</li>
              <li>Product titles and descriptions</li>
              <li>Prices and availability</li>
              <li>Your store name and promotions</li>
            </ul>
            <p className="text-sm text-blue-700 mt-3">
              To update your ads, update your product data in{' '}
              <a
                href="https://merchants.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline hover:text-blue-800"
              >
                Google Merchant Center
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-2">What happens next?</h4>
        <p className="text-sm text-gray-600">
          When you create this ad group, Google will automatically start showing product ads
          for items in your Merchant Center feed that match your targeting criteria.
          You can adjust bidding and targeting settings after creation.
        </p>
      </div>
    </div>
  );
};
